import { Component, Input, TemplateRef } from '@angular/core';

export type TooltipTheme = 'light' | 'dark';
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * @purpose Shared tooltip component that supports both string and template-based tooltips.
 *
 * @usage
 * String tooltip:
 * ```html
 * <lib-shared-tooltip tooltipContent="This is a tooltip">
 *   <button>Hover me</button>
 * </lib-shared-tooltip>
 * ```
 *
 * Template tooltip:
 * ```html
 * <ng-template #customTooltip>
 *   <div class="custom-tooltip">
 *     <strong>Custom Tooltip!</strong>
 *     <p>You can put any HTML content here.</p>
 *   </div>
 * </ng-template>
 *
 * <lib-shared-tooltip [tooltipContent]="customTooltip" tooltipPosition="right" theme="light">
 *   <span>Hover for custom tooltip</span>
 * </lib-shared-tooltip>
 * ```
 */
@Component({
  selector: 'lib-shared-tooltip',
  templateUrl: './shared-tooltip.component.html',
  styleUrls: ['./shared-tooltip.component.scss'],
  standalone: false,
})
export class SharedToolTipComponent {
  /**
   * @purpose Content of the tooltip. Can be a string or a TemplateRef for custom templates.
   */
  @Input() public tooltipContent!: string | TemplateRef<HTMLElement>;

  /**
   * @purpose Position of the tooltip relative to the host element.
   * @default 'top'
   */
  @Input() public tooltipPosition: TooltipPosition = 'top';

  /**
   * @purpose Delay (in ms) before showing the tooltip.
   * @default 50
   */
  @Input() public showDelay = 50;

  /**
   * @purpose Delay (in ms) before hiding the tooltip.
   * @default 100
   */
  @Input() public hideDelay = 100;

  /**
   * @purpose Inline style overrides for the tooltip container.
   */
  @Input() public tooltipStyle: { [key: string]: string } = {};

  /**
   * @purpose Visual theme of the tooltip.
   * @default 'dark'
   */
  @Input() public theme: TooltipTheme = 'dark';

  /**
   * @purpose Extracts tooltip text if content is string, otherwise returns null.
   * @returns {string | null} Tooltip text or null when using TemplateRef.
   */
  public get tooltipText(): string | null {
    return this.isTemplateRef(this.tooltipContent) ? null : this.tooltipContent;
  }

  /**
   * @purpose Type guard to check if content is a TemplateRef.
   * @param content - The tooltip content to check.
   * @returns {boolean} true if content is TemplateRef, false otherwise.
   */
  public isTemplateRef(content: unknown): content is TemplateRef<HTMLElement> {
    return content instanceof TemplateRef;
  }
}
